"use client";

import NextLink from "next/link";
import React, { useRef } from "react";
import { defaultCities } from "../../../../app/common/config";
import { cn } from "../../../../helpers/classNames";
import { isSnowSeason } from "../../../../helpers/season";
import { getRoute } from "../../../../helpers/urls/getRoute";
import { useCountry } from "../../../../hooks/useCountry";
import { AdSlot } from "../../../Ad/slots";
import { Toolbar } from "../../../Ad/slots/Toolbar";
import { AutoSuggest } from "../../../AutoSuggest";
import { Button } from "../../../Button";
import { useDayjs } from "../../../Dayjs/hooks";
import { Favorites } from "../../../Favorites/desktop";
import { MenuDesktop } from "../../../Menu/desktop";
import { WebsiteLogo } from "../../../WebsiteLogo";
import { useStickyHeader } from "../../hooks";
import styles from "./desktop.module.scss";

export const Header: React.FC = () => {
    const country = useCountry();
    const dayjs = useDayjs();
    const headerRef = useRef<HTMLElement>(null);
    const { isSticky, isMenuOpen, toggleMenu } = useStickyHeader({
        headerRef,
    });

    return (
        <>
            <header
                ref={headerRef}
                className={cn(
                    styles.header,
                    isSticky && styles.isSticky,
                    isSticky && isMenuOpen && styles.isOpen
                )}
            >
                <div className={styles.headerInner}>
                    <div className={styles.top}>
                        <WebsiteLogo />
                        <AutoSuggest />
                        <Favorites />
                        <div className={styles.rightButtonWrapper}>
                            {isSnowSeason(dayjs) ? (
                                <NextLink
                                    className={styles.pollenInfo}
                                    href={getRoute({
                                        parameters: {
                                            pageName: "skiAreaList",
                                        },
                                        country,
                                    })}
                                    target="_self"
                                    aria-label="Skigebiete öffnen"
                                    data-label="Skigebiete öffnen"
                                    title="Alle Infos zu Wetter, Neuschnee, Pisten, Lifte sowie Skipasspreise - Wetter ist wetter.com"
                                >
                                    <span className="snow-icon-skiarea" />
                                    <span className={styles.pollenInfoText}>
                                        Skigebiete
                                    </span>
                                </NextLink>
                            ) : (
                                <a
                                    className={styles.pollenInfo}
                                    href={
                                        country === "de"
                                            ? getRoute({
                                                  parameters: {
                                                      pageName:
                                                          "healthPollenLandingpage",
                                                  },
                                                  country,
                                              })
                                            : getRoute({
                                                  parameters: {
                                                      pageName:
                                                          "healthPollenLocationPage",
                                                      code:
                                                          defaultCities[country]
                                                              ?.code ?? "",
                                                      seoString:
                                                          defaultCities[country]
                                                              ?.seoString ?? "",
                                                  },
                                                  country,
                                              })
                                    }
                                    target="_self"
                                    aria-label="Pollenflug"
                                    data-label="Pollenflug"
                                    title="Infos zum Pollenflug in Deiner Region"
                                >
                                    <span className="icon-polle" />
                                    <span className={styles.pollenInfoText}>
                                        Pollenflug
                                    </span>
                                </a>
                            )}
                        </div>
                        {isSticky ? (
                            <Button
                                variant="unstyled"
                                className={styles.menuTrigger}
                                onClick={toggleMenu}
                                aria-label={
                                    isMenuOpen
                                        ? "Menü schließen"
                                        : "Menü öffnen"
                                }
                            >
                                <span
                                    className={
                                        isMenuOpen
                                            ? cn("icon-cross")
                                            : cn("icon-menu")
                                    }
                                >
                                    Menü
                                </span>
                            </Button>
                        ) : null}
                    </div>
                    <div className={styles.navWrapper}>
                        <MenuDesktop />
                    </div>
                </div>
            </header>
            <div className={cn(isSticky && styles.spacer)} />
            <Toolbar />
            <div className={styles.adSlotWrapper}>
                <AdSlot
                    slot={{
                        de: "WET_DE_D_premiumfullbanner",
                        at: "WET_AT_D_premiumfullbanner",
                        ch: "WET_CH_D_premiumfullbanner",
                        pl: undefined,
                    }}
                    minHeight="250px"
                />
            </div>
        </>
    );
};
